<template>
  <v-card elevation="3" class="my-5 card-fixed-height">
    <v-card-title class="card-header py-3 px-4">
      <v-icon color="amber darken-3" class="mr-2 flex-shrink-0">
        {{ icon || "mdi-lightbulb-on-outline" }}
      </v-icon>
      <span class="card-title-text">{{ infoTitle }}</span>
      <v-chip
        v-if="hasData"
        x-small
        color="amber darken-3"
        outlined
        class="ml-2 flex-shrink-0"
      >
        {{ insights.length }}
      </v-chip>
      <v-spacer></v-spacer>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            v-bind="attrs"
            v-on="on"
            class="flex-shrink-0"
            :loading="loading"
            @click="fetchData"
          >
            <v-icon small>mdi-refresh</v-icon>
          </v-btn>
        </template>
        <span>Atualizar insights</span>
      </v-tooltip>
    </v-card-title>

    <v-divider></v-divider>

    <v-card-text class="pa-4 card-content">
      <!-- Estado vazio -->
      <div v-if="!hasData" class="text-center py-6">
        <v-icon size="48" color="grey lighten-1">mdi-lightbulb-outline</v-icon>
        <p class="text-caption grey--text mt-2 mb-0">
          Nenhum insight no momento.
        </p>
        <p class="text-caption grey--text text--lighten-1 mb-0">
          Os insights aparecem automaticamente conforme novos dados do paciente
          chegam.
        </p>
      </div>

      <!-- Lista de insights -->
      <div v-else class="insights-list">
        <div
          v-for="insight in insights"
          :key="insight.id"
          class="insight-item"
          :class="[
            `insight-item--${insight.severity}`,
            { 'insight-item--acted': insight.status === 'acted' },
          ]"
        >
          <div class="insight-head">
            <v-icon :color="severityColor(insight.severity)" small class="mr-2">
              {{ severityIcon(insight.severity) }}
            </v-icon>
            <span class="insight-title">{{ insight.title }}</span>
            <v-chip
              x-small
              :color="severityColor(insight.severity)"
              text-color="white"
              class="ml-2 flex-shrink-0"
            >
              {{ severityLabel(insight.severity) }}
            </v-chip>
            <v-chip
              v-if="insight.status === 'acted'"
              x-small
              color="green darken-1"
              text-color="white"
              class="ml-1 flex-shrink-0"
            >
              <v-icon x-small left>mdi-check</v-icon>
              Tratado
            </v-chip>
          </div>

          <p v-if="insight.body" class="insight-body">{{ insight.body }}</p>

          <div class="insight-actions">
            <span class="insight-date">{{ formatDate(insight.created_at) }}</span>
            <v-spacer></v-spacer>
            <v-btn
              v-if="insight.status !== 'acted'"
              x-small
              text
              color="green darken-1"
              :loading="busyId === insight.id"
              @click="setStatus(insight, 'acted')"
            >
              <v-icon x-small left>mdi-check-circle-outline</v-icon>
              Tratar
            </v-btn>
            <v-btn
              x-small
              text
              color="grey darken-1"
              :loading="busyId === insight.id"
              @click="setStatus(insight, 'dismissed')"
            >
              <v-icon x-small left>mdi-close-circle-outline</v-icon>
              Dispensar
            </v-btn>
          </div>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import {
  getInsightsByPatient,
  updateInsightStatus,
} from "@/services/patientInsightsService";
import { useSnackbar } from "@/composables/useSnackbar";
import patientDataRefresh from "@/features/patients/profile-dashboard/mixins/patientDataRefresh";
import { formatDate } from "@/utils/datetime";
import cardItemCount from "@/features/patients/profile-dashboard/mixins/cardItemCount.js";

export default {
  mixins: [patientDataRefresh, cardItemCount],
  name: "PatientInsightsCard",

  props: {
    // O título vem de config/dashboardCards.js, via a página do dashboard.
    // Sem default de propósito: duplicar o texto aqui já fez a renomeação
    // do card passar despercebida uma vez.
    infoTitle: { type: String, required: true },
    // salvia-781: declarada para não virar atributo solto no DOM. Este card é
    // só-leitura — o registro entra por outro card —, então não oferece o "+".
    showQuickAdd: { type: Boolean, default: false },
    patientId: { type: [String, Number], required: true },
    icon: { type: String, default: null },
  },

  data() {
    return {
      insights: [],
      loading: false,
      busyId: null,
    };
  },

  computed: {
    /** SALVIA-938: itens do card, para o contador da caixa lateral. */
    itemCount() {
      return (this.insights || []).length;
    },
    hasData() {
      return this.insights.length > 0;
    },
    // Sinal reativo de tempo real: IDs das notificações insight.clinical deste
    // paciente que já chegaram ao sino (via Echo). Quando muda, um insight novo
    // foi gerado para este prontuário — refetch sem o médico clicar "Atualizar".
    insightSignal() {
      const items = this.$store?.state?.notifications?.items || [];
      return items
        .filter(
          (n) =>
            n?.type === "insight.clinical" &&
            String(n?.data?.patient_id) === String(this.patientId)
        )
        .map((n) => n.id)
        .join(",");
    },
  },

  watch: {
    patientId: "fetchData",
    // Coalesce rajadas (vários gatilhos ~simultâneos) num único refetch.
    insightSignal() {
      clearTimeout(this._insightRefetchTimer);
      this._insightRefetchTimer = setTimeout(() => this.fetchData(), 400);
    },
  },

  created() {
    this.fetchData().finally(() => this.$emit("card-loaded"));
  },

  beforeDestroy() {
    clearTimeout(this._insightRefetchTimer);
  },

  methods: {
    formatDate,
    async fetchData() {
      if (!this.patientId) return;
      this.loading = true;
      try {
        this.insights = await getInsightsByPatient(this.patientId);
      } catch (error) {
        console.error("Erro ao buscar insights:", error);
        this.insights = [];
      } finally {
        this.loading = false;
      }
    },

    async setStatus(insight, status) {
      this.busyId = insight.id;
      try {
        await updateInsightStatus(insight.id, status);
        const { success, error } = useSnackbar();
        if (status === "dismissed") {
          // Some da lista (o backend passa a excluí-lo por padrão).
          this.insights = this.insights.filter((i) => i.id !== insight.id);
          success("Insight dispensado.");
        } else {
          insight.status = status;
          success("Insight marcado como tratado.");
        }
      } catch (e) {
        console.error("Erro ao atualizar insight:", e);
        useSnackbar().error("Não foi possível atualizar o insight.");
      } finally {
        this.busyId = null;
      }
    },

    severityLabel(severity) {
      return (
        { info: "Info", warning: "Atenção", critical: "Crítico" }[severity] ||
        "Info"
      );
    },

    severityColor(severity) {
      return (
        {
          info: "blue darken-1",
          warning: "orange darken-2",
          critical: "red darken-2",
        }[severity] || "blue darken-1"
      );
    },

    severityIcon(severity) {
      return (
        {
          info: "mdi-information-outline",
          warning: "mdi-alert-outline",
          critical: "mdi-alert-octagon-outline",
        }[severity] || "mdi-information-outline"
      );
    },
  },
};
</script>

<style scoped>
/* ═══ Tema: Âmbar/Dourado (Insights) ═══ */
.card-fixed-height {
  display: flex;
  flex-direction: column;
  border-radius: 14px !important;
  border: 1px solid #fde68a !important;
  box-shadow: 0 2px 10px rgba(217, 119, 6, 0.1) !important;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}

.card-fixed-height:hover {
  border-color: #fcd34d !important;
  box-shadow: 0 6px 20px rgba(217, 119, 6, 0.18) !important;
}

.card-header {
  display: flex !important;
  flex-wrap: nowrap !important;
  align-items: center !important;
  min-width: 0;
  background: #fffbeb;
  border-bottom: 1px solid #fde68a;
}

.card-title-text {
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.4;
  color: #b45309;
  letter-spacing: 0.3px;
}

/* Os dois cards de largura cheia (Historico de Vida e Insights) NAO usam a
   altura fixa de 320px dos demais; eles reproduzem o tamanho antigo do
   Historico de Vida, um pouco mais baixo: cabecalho + 200px de corpo.
   `height` e nao `max-height` de proposito — com max-height o card encolhia
   quando havia pouco conteudo, e os dois, que ficam um embaixo do outro em
   largura cheia, apareciam com alturas diferentes. */
.card-content {
  height: 200px;
  overflow-y: auto;
}

.card-content::-webkit-scrollbar {
  width: 6px;
}
.card-content::-webkit-scrollbar-thumb {
  background: #fcd34d;
  border-radius: 10px;
}
.card-content::-webkit-scrollbar-thumb:hover {
  background: #b45309;
}

.insights-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.insight-item {
  border: 1px solid #eee;
  border-left: 4px solid #cbd5e1;
  border-radius: 10px;
  padding: 10px 12px;
  background: #fff;
}

.insight-item--info {
  border-left-color: #1e88e5;
}
.insight-item--warning {
  border-left-color: #ef6c00;
  background: #fffdf7;
}
.insight-item--critical {
  border-left-color: #c62828;
  background: #fffafa;
}
.insight-item--acted {
  opacity: 0.7;
}

.insight-head {
  display: flex;
  align-items: center;
  min-width: 0;
}

.insight-title {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.35;
  color: #334155;
  overflow-wrap: break-word;
}

.insight-body {
  font-size: 12px;
  color: #64748b;
  line-height: 1.45;
  margin: 6px 0 4px;
}

.insight-actions {
  display: flex;
  align-items: center;
  margin-top: 2px;
}

.insight-date {
  font-size: 11px;
  color: #94a3b8;
}
</style>
